import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Tag, Button, Space, Descriptions, Spin, message, Modal, InputNumber, Form, Input, Upload, Alert } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, PlusOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getInventaireById, validateInventaire, populateInventaireFromStock, upsertInventaireDetail, deleteInventaireDetail, importInventaireDetails, updateInventaire } from '../../models/inventaireAPI';
import EditableDateField from '../../utils/EditableDateField';
import NestedAchatTable from '../achat/NestedAchatTable';
import ExcelDetailImport from '../shared/ExcelDetailImport';
import { formatAmount } from '../../utils/formatAmount';

const SingleInventaire = ({ id, onBack }) => {
  const [inventaire, setInventaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [populating, setPopulating] = useState(false);
  const [addDetailModalVisible, setAddDetailModalVisible] = useState(false);
  const [addDetailForm] = Form.useForm();
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);

  useEffect(() => {
    fetchInventaire();
  }, [id]);

  const fetchInventaire = async () => {
    try {
      setLoading(true);
      const response = await getInventaireById(id);
      
      if (response.data.success) {
        setInventaire(response.data.data);
      }
    } catch (error) {
      message.error('Erreur lors du chargement de l\'inventaire');
      console.error('Error fetching inventaire:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = () => {
    Modal.confirm({
      title: 'Valider l\'inventaire',
      content: 'Êtes-vous sûr de vouloir valider cet inventaire? Les stocks seront mis à jour selon les quantités enregistrées.',
      okText: 'Oui, valider',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await validateInventaire(id);
          message.success('Inventaire validé avec succès');
          fetchInventaire();
        } catch (error) {
          message.error(`Erreur: ${error.response?.data?.error || error.message}`);
        }
      }
    });
  };

  const handlePopulateFromStock = () => {
    Modal.confirm({
      title: 'Utiliser le stock actuel',
      content: 'Cette action va remplacer tous les détails existants par le stock actuel de l\'entrepôt. Continuer?',
      okText: 'Oui',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          setPopulating(true);
          await populateInventaireFromStock(id);
          message.success('Inventaire rempli avec le stock actuel');
          fetchInventaire();
        } catch (error) {
          message.error(`Erreur: ${error.response?.data?.error || error.message}`);
        } finally {
          setPopulating(false);
        }
      }
    });
  };

  const handleImport = () => {
    setImportModalVisible(true);
  };

  const handleImportFile = async () => {
    if (!importFile) {
      message.error('Veuillez sélectionner un fichier');
      return;
    }

    try {
      setImporting(true);
      const response = await importInventaireDetails(id, importFile);
      
      if (response.data.success) {
        const { success, errors, created, updated } = response.data;
        message.success(
          `Import réussi: ${success} articles traités (${created} créés, ${updated} mis à jour)${errors > 0 ? `, ${errors} erreurs` : ''}`
        );
        setImportModalVisible(false);
        setImportFile(null);
        fetchInventaire();
      }
    } catch (error) {
      message.error(`Erreur lors de l'import: ${error.response?.data?.error || error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleExcelImport = async (importedItems) => {
    let ok = 0;
    let failed = 0;
    for (const item of importedItems) {
      try {
        await upsertInventaireDetail(id, {
          CODE_ART: item.CODE_ART,
          ARTICLE: item.ARTICLE || '',
          QTE_INV: item.QTE_INV || 0,
          PA_HT: item.PA_HT,
          PA_TTC: item.PA_TTC,
        });
        ok += 1;
      } catch (error) {
        failed += 1;
      }
    }
    if (ok > 0) {
      message.success(`${ok} article(s) importé(s)${failed > 0 ? `, ${failed} en erreur` : ''}`);
    } else {
      message.error("Aucun article n'a pu être importé");
    }
    await fetchInventaire();
  };

  const handleAddDetail = async (values) => {
    try {
      await upsertInventaireDetail(id, {
        CODE_ART: values.codeArt,
        ARTICLE: values.article || '',
        QTE_INV: values.qteStock || 0,
        PA_HT: values.paHT,
        PA_TTC: values.paTTC
      });
      message.success('Article ajouté avec succès');
      setAddDetailModalVisible(false);
      addDetailForm.resetFields();
      fetchInventaire();
    } catch (error) {
      message.error(`Erreur: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleSave = async (record) => {
    try {
      await upsertInventaireDetail(id, {
        CODE_ART: record.CODE_ART,
        ARTICLE: record.ARTICLE,
        QTE_INV: record.QTE,
        PA_HT: record.PA_HT,
        PA_TTC: record.PA_TTC
      });
      message.success('Détail mis à jour');
      await fetchInventaire();
    } catch (error) {
      message.error(`Erreur: ${error.response?.data?.error || error.message}`);
      await fetchInventaire();
    }
  };

  const handleDelete = async (codeArt) => {
    try {
      await deleteInventaireDetail(id, codeArt);
      message.success('Article supprimé');
      fetchInventaire();
    } catch (error) {
      message.error(`Erreur: ${error.response?.data?.error || error.message}`);
    }
  };

  const tableItems = useMemo(
    () => (inventaire?.details || []).map((detail) => ({
      ...detail,
      QTE: detail.QTE_INV,
    })),
    [inventaire?.details]
  );

  const totals = useMemo(
    () => (inventaire?.details || []).reduce(
      (acc, detail) => ({
        quantity: acc.quantity + (Number(detail.QTE_INV) || 0),
        stock: acc.stock + (Number(detail.QTE_STOCK_ACTUEL) || 0),
        totalHT: acc.totalHT + (Number(detail.QTE_INV) || 0) * (Number(detail.PA_HT) || 0),
        totalTTC: acc.totalTTC + (Number(detail.QTE_INV) || 0) * (Number(detail.PA_TTC) || 0),
      }),
      { quantity: 0, stock: 0, totalHT: 0, totalTTC: 0 }
    ),
    [inventaire?.details]
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!inventaire) {
    return (
      <Card>
        <p>Inventaire non trouvé</p>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Retour
        </Button>
      </Card>
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
              Retour
            </Button>
            <h2 style={{ margin: 0 }}>Inventaire {inventaire.CODE_INV}</h2>
            {inventaire.VALID ? (
              <Tag color="green">Validé</Tag>
            ) : (
              <Tag color="orange">Brouillon</Tag>
            )}
            {inventaire.isActive && (
              <Tag color="blue">Actif</Tag>
            )}
          </Space>
          {!inventaire.VALID && (
            <PermissionButton permissionModule="inventory" permissionAction="validate"
              type="primary"
              icon={<CheckCircleOutlined />}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              onClick={handleValidate}
            >
              Valider l'inventaire
            </PermissionButton>
          )}
        </div>

        <Descriptions bordered column={3}>
          <Descriptions.Item label="Code Inventaire">
            {inventaire.CODE_INV}
          </Descriptions.Item>
          <Descriptions.Item label="Entrepôt">
            {inventaire.entrepot?.code_ent} - {inventaire.entrepot?.name}
          </Descriptions.Item>
          <Descriptions.Item label="Année">
            {inventaire.YEAR}
          </Descriptions.Item>
          <Descriptions.Item label="Montant HT">
            {formatAmount(inventaire.MNT_HT, 2)} DH
          </Descriptions.Item>
          <Descriptions.Item label="Montant TTC">
            {formatAmount(inventaire.MNT_TTC, 2)} DH
          </Descriptions.Item>
          <Descriptions.Item label="Nombre d'articles">
            {inventaire.details?.length || 0}
          </Descriptions.Item>
          <Descriptions.Item label="Date de création">
            <EditableDateField
              value={inventaire.createdAt}
              onChange={async (newDate) => {
                try {
                  await updateInventaire(id, { createdAt: newDate });
                  message.success('Date de création mise à jour');
                  fetchInventaire();
                } catch (error) {
                  message.error(`Erreur: ${error.response?.data?.error || error.message}`);
                }
              }}
              disabled={!!inventaire.VALID}
              withTime
            />
          </Descriptions.Item>
          {inventaire.DATE_VALID && (
            <Descriptions.Item label="Date de validation">
              {dayjs(inventaire.DATE_VALID).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
          )}
          {inventaire.REMARQUE && (
            <Descriptions.Item label="Remarque" span={3}>
              {inventaire.REMARQUE}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card 
        title="Détails de l'inventaire"
        extra={
          !inventaire.VALID && (
            <Space>
              <ExcelDetailImport
                type="inventaire"
                onImport={handleExcelImport}
              />
              {(inventaire.details?.length === 0 || !inventaire.details) && (
                <>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handlePopulateFromStock}
                    loading={populating}
                  >
                    Utiliser le stock actuel
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={handleImport}
                  >
                    Importer
                  </Button>
                </>
              )}
              <PermissionButton permissionModule="inventory" permissionAction="update"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddDetailModalVisible(true)}
              >
                Ajouter un article
              </PermissionButton>
            </Space>
          )
        }
      >
        <NestedAchatTable
          items={tableItems}
          handleSave={handleSave}
          handleDelete={handleDelete}
          validated={Boolean(inventaire.VALID)}
          type="inventaire"
          showStock={false}
          codeEnt={inventaire.CODE_ENT}
        />
        <Descriptions
          bordered
          size="small"
          column={4}
          style={{ marginTop: 12 }}
          labelStyle={{ fontWeight: 'bold' }}
        >
          <Descriptions.Item label="Quantité totale">
            {formatAmount(totals.quantity, 0)}
          </Descriptions.Item>
          <Descriptions.Item label="Stock total">
            {formatAmount(totals.stock, 0)}
          </Descriptions.Item>
          <Descriptions.Item label="Total HT">
            {formatAmount(totals.totalHT, 2)} DH
          </Descriptions.Item>
          <Descriptions.Item label="Total TTC">
            {formatAmount(totals.totalTTC, 2)} DH
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Modal
        title="Ajouter un article"
        open={addDetailModalVisible}
        onCancel={() => {
          setAddDetailModalVisible(false);
          addDetailForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={addDetailForm}
          layout="vertical"
          onFinish={handleAddDetail}
        >
          <Form.Item
            name="codeArt"
            label="Code Article"
            rules={[{ required: true, message: 'Code article requis' }]}
          >
            <Input placeholder="Code article" />
          </Form.Item>
          <Form.Item
            name="article"
            label="Désignation"
          >
            <Input placeholder="Désignation (optionnel)" />
          </Form.Item>
          <Form.Item
            name="qteStock"
            label="Qté Inv"
            rules={[{ required: true, message: 'Quantité requise' }]}
            initialValue={0}
          >
            <InputNumber
              min={0}
              step={1}
              precision={0}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="paHT"
            label="PA HT"
            rules={[{ required: true, message: 'PA HT requis' }]}
            initialValue={0}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="paTTC"
            label="PA TTC"
            rules={[{ required: true, message: 'PA TTC requis' }]}
            initialValue={0}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <PermissionButton   onClick={() => {
                setAddDetailModalVisible(false);
                addDetailForm.resetFields();
              }}>
                Annuler
              </PermissionButton>
              <PermissionButton permissionModule="inventory" permissionAction="update" type="primary" htmlType="submit">
                Ajouter
              </PermissionButton>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Importer les détails de l'inventaire"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportFile(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setImportModalVisible(false);
            setImportFile(null);
          }}>
            Annuler
          </Button>,
          <Button
            key="import"
            type="primary"
            onClick={handleImportFile}
            loading={importing}
            disabled={!importFile}
          >
            Importer
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Format de fichier supporté"
            description="Vous pouvez importer un fichier Excel (.xlsx, .xls) ou DBF (.dbf). Le fichier doit contenir les colonnes suivantes: CODE_ART (requis), QTE_INV, PA_HT, PA_TTC, ARTICLE (optionnel)."
            type="info"
            showIcon
          />
          
          <Upload
            beforeUpload={(file) => {
              const fileName = file.name.toLowerCase();
              const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
              const isDBF = fileName.endsWith('.dbf');
              
              if (!isExcel && !isDBF) {
                message.error('Veuillez uploader un fichier Excel (.xlsx, .xls) ou DBF (.dbf)');
                return Upload.LIST_IGNORE;
              }
              
              setImportFile(file);
              return false;
            }}
            onRemove={() => {
              setImportFile(null);
            }}
            fileList={importFile ? [importFile] : []}
            accept=".xlsx,.xls,.dbf,.DBF"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>
              Sélectionner un fichier
            </Button>
          </Upload>

          {importFile && (
            <Alert
              message={`Fichier sélectionné: ${importFile.name}`}
              type="success"
              showIcon
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default SingleInventaire;



